<template>
  <div class="pdv-timeline-wrap" v-loading="loading">
    <el-timeline class="pdv-timeline">
      <el-timeline-item
        v-for="(item, index) of tableData"
        :key="index"
        :timestamp="item.timestamp"
        placement="top"
        :color="eventColor(item)"
        :icon="item.icon"
        size="large"
      >
        <article class="timeline-card" :class="eventClass(item)">
          <header class="timeline-card__header">
            <div class="timeline-card__info">
              <span class="timeline-card__badge">{{ eventLabel(item) }}</span>
              <h6 class="timeline-card__title">{{ item.content.title }}</h6>
              <p class="timeline-card__description" v-if="item.content.description">
                {{ item.content.description }}
              </p>
            </div>
            <div class="timeline-card__value" v-if="item.content.value">
              {{ item.content.value }}
            </div>
          </header>

          <section
            class="timeline-section"
            v-if="item.content.detail && item.content.detail.prepost && item.content.detail.prepost.length > 0"
          >
            <h6 class="timeline-section__title">Postagens</h6>
            <div class="timeline-table">
              <div class="timeline-table__row timeline-table__head">
                <span>Etiqueta</span>
                <span>Serviço</span>
              </div>
              <div
                v-for="prepostData of item.content.detail.prepost"
                :key="prepostData.tag"
                class="timeline-table__row"
                :class="{ 'timeline-table__row--removed': prepostData.state === 'remove' }"
              >
                <span class="timeline-table__tag">
                  {{ prepostData.tag }}
                  <el-tag v-if="prepostData.state === 'remove'" size="mini" type="danger">Removido</el-tag>
                </span>
                <span class="timeline-table__service">{{ prepostData.service }}</span>
                <span class="timeline-table__amount">{{ prepostData.pricetable }}</span>
              </div>
            </div>
          </section>

          <section
            class="timeline-section"
            v-if="item.content.detail && item.content.detail.reverse && item.content.detail.reverse.length > 0"
          >
            <h6 class="timeline-section__title">Reversa</h6>
            <div class="timeline-table timeline-table--reverse">
              <div class="timeline-table__row timeline-table__head">
                <span>Autorização</span>
                <span>Nome</span>
              </div>
              <div
                v-for="(reverseData, reverseIndex) of item.content.detail.reverse"
                :key="reverseData.authorization || reverseData.id || reverseIndex"
                class="timeline-table__row"
              >
                <span class="timeline-table__tag">
                  {{ reverseData.authorization || reverseData.id }}
                </span>
                <span class="timeline-table__service">{{ reverseData.name }}</span>
                <span class="timeline-table__amount">{{ reverseData.content }}</span>
              </div>
            </div>
          </section>

          <section
            class="timeline-section"
            v-if="normalizePlpRows(item.content.detail && item.content.detail.plp).length > 0"
          >
            <h6 class="timeline-section__title">PLP Terceiros</h6>
            <div class="timeline-table timeline-table--plp">
              <div class="timeline-table__row timeline-table__head">
                <span>PLP</span>
                <span>Etiqueta</span>
              </div>
              <div
                v-for="(plpRow, plpIndex) of normalizePlpRows(item.content.detail.plp)"
                :key="`${plpRow.plp}-${plpRow.tag}-${plpIndex}`"
                class="timeline-table__row"
              >
                <span class="timeline-table__tag">{{ plpRow.plp }}</span>
                <span class="timeline-table__service">{{ plpRow.tag }}</span>
                <span class="timeline-table__amount">{{ plpRow.service || '—' }}</span>
              </div>
            </div>
          </section>

          <section
            class="timeline-section timeline-section--adjustments"
            v-if="item.content.aditional > 0 || item.content.discount > 0"
          >
            <h6 class="timeline-section__title">Ajustes do Atendimento</h6>
            <div class="timeline-adjustments">
              <div class="timeline-inline-metric" v-if="item.content.aditional > 0">
                <span>Adicional</span>
                <strong class="amount--positive">R$ {{ formatMoney(item.content.aditional) }}</strong>
              </div>
              <div class="timeline-inline-metric" v-if="item.content.discount > 0">
                <span>Desconto</span>
                <strong class="amount--discount">- R$ {{ formatMoney(item.content.discount) }}</strong>
              </div>
            </div>
          </section>

          <PdvUserTimelineClose
            v-if="item.content.detail && item.content.detail.money"
            :item="item.content.detail"
          />
        </article>
      </el-timeline-item>
    </el-timeline>
  </div>
</template>
<script>
import { PdvDashboardService } from "@/services/pdv/pdv.dashboard.services";
import moment from "moment";
import "moment/locale/pt-br";
import PdvUserTimelineClose from "./PdvUserTimelineClose.vue";

export default {
  name: "PdvUserTimeline",
  props: ["startedAt", "userId"],
  components: {
    PdvUserTimelineClose,
  },
  data() {
    return {
      loading: false,
      tableData: [],
    };
  },
  methods: {
    fetch() {
      const vm = this;
      vm.loading = true;
      const s = moment(vm.startedAt);
      PdvDashboardService.timeline(s.format("YYYY-MM-DD"), vm.userId)
        .then((response) => {
          if (response.length === 0) {
            return this.$message({
              type: "warning",
              message: "Usuário sem eventos de atendimento!",
              showClose: true,
            });
          }
          vm.tableData = response;
        })
        .catch((error) => {
          this.$message({ type: "error", duration: 5000, message: error, showClose: true });
        })
        .finally(() => (vm.loading = false));
    },
    eventClass(item) {
      if (item.content.title === "Abertura do caixa") return "timeline-card--open";
      if (item.content.title === "Fechamento do caixa") return "timeline-card--close";
      if (item.content.tag === "danger") return "timeline-card--subtract";
      if (item.content.tag === "success") return "timeline-card--add";
      return "timeline-card--call";
    },
    eventColor(item) {
      const colors = {
        green: "#67C23A",
        blue: "#409EFF",
        red: "#E6A23C",
      };
      return colors[item.color] || "#909399";
    },
    eventLabel(item) {
      if (item.content.title === "Abertura do caixa") return "Abertura";
      if (item.content.title === "Fechamento do caixa") return "Fechamento";
      if (item.content.tag === "danger") return "Saída";
      if (item.content.tag === "success") return "Entrada";
      return "Atendimento";
    },
    formatMoney(value) {
      return Number(value).toFixed(2).replace(".", ",");
    },
    normalizePlpRows(plpList) {
      const rows = [];

      for (const item of plpList || []) {
        if (!item || typeof item !== "object") {
          continue;
        }

        const plpNumber = String(item.plp != null ? item.plp : "0").trim();

        if (Array.isArray(item.tags) && item.tags.length > 0) {
          for (const tagData of item.tags) {
            if (!tagData || typeof tagData !== "object") {
              continue;
            }
            rows.push({
              plp: plpNumber,
              tag: tagData.tag || tagData.id || "",
              service: tagData.serviceCode || tagData.service || tagData.marketplace || "",
            });
          }
          continue;
        }

        if (item.tag) {
          rows.push({
            plp: plpNumber,
            tag: item.tag,
            service: item.marketplace || item.service || item.serviceCode || "",
          });
        }
      }

      return rows;
    },
  },
};
</script>
<style scoped>
.pdv-timeline-wrap {
  padding: 4px 0 12px;
}

.pdv-timeline {
  padding-left: 4px;
}

.timeline-card {
  border: 1px solid #e4e7ed;
  border-left-width: 4px;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
}

.timeline-card--open {
  border-left-color: #67c23a;
}

.timeline-card--close {
  border-left-color: #e6a23c;
  background: #fffaf3;
}

.timeline-card--call {
  border-left-color: #409eff;
}

.timeline-card--add {
  border-left-color: #67c23a;
}

.timeline-card--subtract {
  border-left-color: #f56c6c;
}

.timeline-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
  border-bottom: 1px solid #f0f2f5;
}

.timeline-card--close .timeline-card__header {
  border-bottom-color: #faecd8;
}

.timeline-card__badge {
  display: inline-block;
  margin-bottom: 6px;
  padding: 2px 8px;
  border-radius: 10px;
  background: #f4f4f5;
  color: #606266;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.timeline-card--open .timeline-card__badge {
  background: #f0f9eb;
  color: #67c23a;
}

.timeline-card--close .timeline-card__badge {
  background: #fdf6ec;
  color: #e6a23c;
}

.timeline-card--call .timeline-card__badge {
  background: #ecf5ff;
  color: #409eff;
}

.timeline-card--add .timeline-card__badge {
  background: #f0f9eb;
  color: #67c23a;
}

.timeline-card--subtract .timeline-card__badge {
  background: #fef0f0;
  color: #f56c6c;
}

.timeline-card__title {
  margin: 0;
  color: #303133;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.4;
}

.timeline-card__description {
  margin: 6px 0 0;
  color: #909399;
  font-size: 12px;
  line-height: 1.5;
}

.timeline-card__value {
  flex-shrink: 0;
  padding: 6px 12px;
  border-radius: 6px;
  background: #f5f7fa;
  color: #303133;
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
}

.timeline-card--close .timeline-card__value {
  background: #fdf6ec;
  color: #b88230;
}

.timeline-section {
  padding: 12px 16px 14px;
  border-top: 1px solid #f0f2f5;
}

.timeline-section--adjustments {
  background: #fafcff;
}

.timeline-adjustments {
  display: grid;
  gap: 8px;
}

.timeline-section__title {
  margin: 0 0 10px;
  color: #606266;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
}

.timeline-table {
  border: 1px solid #ebeef5;
  border-radius: 6px;
  overflow: hidden;
}

.timeline-table__row {
  display: grid;
  grid-template-columns: 1.2fr 1.6fr 0.8fr;
  gap: 8px;
  align-items: center;
  padding: 8px 12px;
  border-bottom: 1px solid #f0f2f5;
  font-size: 12px;
}

.timeline-table__row:last-child {
  border-bottom: none;
}

.timeline-table__row:nth-child(even) {
  background: #fafafa;
}

.timeline-table__head {
  background: #f5f7fa;
  color: #909399;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
}

.timeline-table__tag {
  color: #606266;
  font-weight: 600;
}

.timeline-table__service {
  color: #909399;
}

.timeline-table__amount {
  color: #409eff;
  font-weight: 700;
  text-align: right;
}

.timeline-table__row--removed .timeline-table__amount {
  color: #f56c6c;
  text-decoration: line-through;
}

.timeline-inline-metric {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  background: #fff;
  font-size: 12px;
}

.timeline-inline-metric span {
  color: #909399;
  font-weight: 600;
}

.timeline-inline-metric strong {
  font-size: 13px;
}

.amount--positive {
  color: #409eff;
}

.amount--discount {
  color: #f56c6c;
}
</style>
